import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { FCX, Tooltip, useUuid } from '@datassential/platform-ui-lib';
import { ISocialPost, SocialPosts } from '@models/SocialPosts';
import ExpandableSocialPostCard from '@components/SocialPostCard/ExpandableSocialPostCard';
import CopyButton from '@components/new/Table/CopyButton';
import CopyNotification from '@components/new/Table/CopyNotification';
import useNotification from '@hooks/useNotification';
import { trimInnerHtml, trimLineBreaks } from '@helpers';
import Loader from '@components/new/Loader/Loader';
import './BrandPerceptionsPosts.scss';

enum ChainTab {
    Primary = 'primary',
    Competitor = 'competitor',
}

interface BrandPerceptionsPostsProps {
    posts: ISocialPost[];
    competitorPosts?: ISocialPost[];
    chainName: string;
    competitorName?: string;
    hasData: boolean;
    period?: string;
    isLoading?: boolean;
}

const COPY_DATA_CONFIG: Array<{
    key: keyof ISocialPost;
    title: string;
    valueFormatter?: (value: string) => string;
}> = [
    { key: 'source', title: 'Source' },
    { key: 'sentiment', title: 'Sentiment' },
    { key: 'engagement', title: 'Engagement Count' },
    { key: 'date', title: 'Date', valueFormatter: SocialPosts.formatDate },
    { key: 'text', title: 'Description', valueFormatter: (value) => trimLineBreaks(trimInnerHtml(value)) },
    { key: 'url', title: 'Post URL' },
];

const BrandPerceptionsPosts: FCX<BrandPerceptionsPostsProps> = ({
    posts,
    competitorPosts,
    chainName,
    competitorName,
    hasData,
    period,
    isLoading = false,
}) => {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const notification = useNotification();
    const primaryTabTooltipId = useUuid();
    const competitorTabTooltipId = useUuid();
    const hasCompetitor = useMemo(
        () => !!competitorName && !!competitorPosts,
        [competitorName, competitorPosts]
    );
    const [activeChain, setActiveChain] = useState<ChainTab>(ChainTab.Primary);

    useEffect(() => {
        if (!hasCompetitor) setActiveChain(ChainTab.Primary);
    }, [hasCompetitor]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [competitorName, period]);

    const handleChainSwitch = useCallback((chain: ChainTab) => {
        setActiveChain(chain);
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, []);

    const activePosts = useMemo(
        () => activeChain === ChainTab.Competitor && competitorPosts ? competitorPosts : posts,
        [activeChain, posts, competitorPosts]
    );

    const copyData = useMemo(
        (): ComponentProps<typeof CopyButton>['data'] => [
            COPY_DATA_CONFIG.map(({ title }) => title),
            ...activePosts.map(post =>
                COPY_DATA_CONFIG.map(({ key, valueFormatter }) => {
                    const value = post[key];
                    return valueFormatter ? valueFormatter(`${value}`) : `${value}`;
                })
            ),
        ],
        [activePosts]
    );

    return (
        <div className="BrandPerceptionsPosts" data-testid="blockBrandPerceptionsPosts">
            <div className="BrandPerceptionsPosts__header HomeBrandPerceptions__panel-header">
                <span className="BrandPerceptionsPosts__title">TOP ENGAGEMENT POSTS</span>
                {hasData && activePosts.length > 0 && (
                    <div onClick={notification.handleShowNotification}>
                        <CopyButton
                            data={copyData}
                            className="appears"
                            testId="buttonBrandPerceptionsCopyPosts"
                        />
                    </div>
                )}
            </div>
            <div className="BrandPerceptionsPosts__toolbar">
                <span className="BrandPerceptionsPosts__period">{period ?? 'Past 90 days'}</span>
                {hasCompetitor && (
                    <div className="BrandPerceptionsPosts__chain-tabs">
                        <button
                            className={cn('BrandPerceptionsPosts__chain-tab', activeChain === ChainTab.Primary && 'is-active')}
                            onClick={() => handleChainSwitch(ChainTab.Primary)}
                            data-tooltip-id={primaryTabTooltipId}
                        >
                            <span className="BrandPerceptionsPosts__chain-tab-text">{chainName}</span>
                        </button>
                        <button
                            className={cn('BrandPerceptionsPosts__chain-tab', activeChain === ChainTab.Competitor && 'is-active')}
                            onClick={() => handleChainSwitch(ChainTab.Competitor)}
                            data-tooltip-id={competitorTabTooltipId}
                        >
                            <span className="BrandPerceptionsPosts__chain-tab-text">{competitorName}</span>
                        </button>
                        <Tooltip id={primaryTabTooltipId} place="top">{chainName}</Tooltip>
                        <Tooltip id={competitorTabTooltipId} place="top">{competitorName}</Tooltip>
                    </div>
                )}
            </div>
            <div ref={scrollRef} className="BrandPerceptionsPosts__scroll">
                {isLoading && <Loader className="BrandPerceptionsPosts__loader" />}
                {!isLoading && hasData && activePosts.length > 0 && (
                    <div key={`${activeChain}-${competitorName ?? 'none'}`} className="BrandPerceptionsPosts__grid appears del-1">
                        {activePosts.map((post) => (
                            <ExpandableSocialPostCard
                                key={post.id}
                                post={post}
                                scrollRef={scrollRef}
                                isDark
                                className="BrandPerceptionsPosts__card"
                                expandedClassName="BrandPerceptionsPosts__card-expanded"
                                testIdPrefix="brandPerceptionsPost"
                            />
                        ))}
                    </div>
                )}
                {!isLoading && (!hasData || activePosts.length === 0) && (
                    <div className="BrandPerceptionsPosts__grid">
                        {Array.from({ length: 2 }).map((_, index) => (
                            <div key={`placeholder-${index}`} className="BrandPerceptionsPosts__card">
                                <div className="BrandPerceptionsPosts__card-inner is-skeleton">
                                    <div className="BrandPerceptionsPosts__skeleton-body"/>
                                    <div className="BrandPerceptionsPosts__skeleton-footer"/>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <CopyNotification isOpen={notification.isOpen}/>
        </div>
    );
};

export default BrandPerceptionsPosts;
